import type { EventFormProps } from '../EventForm';
import type { State } from '@/types/EMRALD_Model';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Tooltip,
} from '@mui/material';
import { type Ref, useEffect, useState } from 'react';
import { useDrop } from 'react-dnd';
import {
  StyledTableCell,
  StyledTableRow,
} from '@/components/forms/ActionForm/ActionToStateTable';
import { useEventFormContext } from '../EventFormContext';

export const StateChange: React.FC<EventFormProps> = ({ eventData }) => {
  const { setTypeProperties, sync } = useEventFormContext();

  const [ifInState, setIfInState] = useState(true);
  const [triggerStates, setTriggerStates] = useState<string[] | undefined>();
  const [allItems, setAllItems] = useState(true);
  const [evalEvOnStateEntry, setEvalEvOnStateEntry] = useState(true);

  useEffect(() => {
    setIfInState(eventData?.ifInState ?? true);
    setTriggerStates(eventData?.triggerStates);
    setAllItems(eventData?.allItems ?? true);
    if (eventData?.evalEvOnStateEntry === undefined) {
      setEvalEvOnStateEntry(ifInState);
    } else {
      setEvalEvOnStateEntry(eventData.evalEvOnStateEntry);
    }
    setTypeProperties(['ifInState', 'triggerStates', 'allItems', 'evalEvOnStateEntry']);
  }, []);

  useEffect(() => {
    sync({ ifInState, triggerStates, allItems, evalEvOnStateEntry });
  }, [ifInState, triggerStates, allItems, evalEvOnStateEntry]);

  const [{ isOver }, drop] = useDrop({
    accept: 'State',
    drop: (item: State) => {
      if (!triggerStates) {
        setTriggerStates([item.name]);
      } else if (!triggerStates.includes(item.name)) {
        setTriggerStates([...triggerStates, item.name]);
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
    }),
  });
  const backgroundColor = isOver
    ? 'var(--emrald-drop-over)'
    : 'var(--emrald-surface)';
  const removeTriggerState = (name: string) => {
    let newTriggerStates = triggerStates;
    if (newTriggerStates) {
      newTriggerStates = newTriggerStates.filter(state => state !== name);
      setTriggerStates(newTriggerStates);
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: ifInState ? 'flex-start' : 'flex-end',
        }}
      >
        <RadioGroup
          name="radio-buttons-group"
          value={ifInState}
          onChange={e => {
            setIfInState(e.target.value === 'true');
            setEvalEvOnStateEntry(e.target.value === 'true');
          }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <FormControlLabel
            value="true"
            control={<Radio />}
            label="On Enter State/s"
            checked={ifInState}
          />
          <FormControlLabel
            value="false"
            control={<Radio />}
            label="On Exit State/s"
            checked={!ifInState}
          />
        </RadioGroup>
        <FormControlLabel
          label={ifInState ? 'Or already in state' : 'Or already out of state'}
          control={
            <Checkbox
              checked={evalEvOnStateEntry ? true : false}
              onChange={e => {
                setEvalEvOnStateEntry(e.target.checked);
              }}
            />
          }
        />
      </div>
      <FormControlLabel
        label="All Items"
        value={allItems}
        control={
          <Checkbox
            checked={allItems ? true : false}
            onChange={e => {
              setAllItems(e.target.checked);
            }}
          />
        }
      />
      <Box
        ref={drop as unknown as Ref<unknown>}
        sx={{ mt: 3 }}
        style={{
          height: '100%',
          backgroundColor,
        }}
      >
        {triggerStates && triggerStates.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>State</StyledTableCell>
                  <StyledTableCell>Command</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {triggerStates.map((name, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell>{name}</StyledTableCell>
                    <StyledTableCell>
                      <Tooltip title="Delete Row">
                        <DeleteIcon
                          sx={{ cursor: 'pointer', ml: 3 }}
                          onClick={() => {
                            removeTriggerState(name);
                          }}
                        />
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              border: '2px dashed gray',
              height: '75px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontWeight: 'bold',
            }}
          >
            Drop State Items Here
          </Box>
        )}
      </Box>
    </>
  );
};
